import { useState, memo } from 'react'

import isEqual from 'react-fast-compare'
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Label,
} from 'recharts'

import { AnalyticsResponse } from '@tribeplatform/gql-client/types'

import type { GroupBy } from '../types/index.js'
import {
  PIE_INACTIVE_OPACITY,
  CHART_ANIMATION_ACTIVE,
  PrimaryPieColors,
  CHART_ANIMATION_DURATION,
  PIE_STROKE_COLOR,
} from '../utils/constants.js'
import { rechartsPieChartParser } from '../utils/parser.js'

import { PieChartLabel } from './PieChartLabel.js'
import { PieChartLegend } from './PieChartLegend.js'
import { PieChartToolTipHOC } from './PieChartToolTip.js'

interface Props {
  data: {
    chartData: AnalyticsResponse
    indexBy: string[]
    labels: string[]
    groupBy?: GroupBy[]
    ColorTheme?: string[]
  }
}

export const PieChartRecharts = memo(
  ({
    data: {
      chartData,
      indexBy,
      labels,
      groupBy,
      ColorTheme = PrimaryPieColors,
    },
  }: Props) => {
    const [activeIndex, setActiveIndex] = useState(-1)
    const parsedData = rechartsPieChartParser(chartData, indexBy, groupBy)

    const isAllZero = (data: ReturnType<typeof rechartsPieChartParser>) => {
      return data.reduce((output, pieCell) => {
        if (pieCell.value !== 0) {
          return false
        }

        return output
      }, true)
    }

    const calculateSum = () => {
      return indexBy.reduce((sum, fieldName) => {
        const fieldData = parsedData.find(data => data.label === fieldName)
        return sum + (fieldData?.value ?? 0)
      }, 0)
    }

    const getOpacity = (index: number) => {
      if (activeIndex === -1) {
        return 1
      }

      if (index === activeIndex) {
        return 1
      }

      return PIE_INACTIVE_OPACITY
    }

    return (
      <div className="flex flex-row items-center">
        <div className="flex-1">
          <ResponsiveContainer width="99%" height="99%" aspect={1}>
            <RechartsPieChart margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
              <Pie
                data={parsedData}
                dataKey="value"
                innerRadius="55%"
                paddingAngle={1}
                isAnimationActive={CHART_ANIMATION_ACTIVE}
                animationDuration={CHART_ANIMATION_DURATION}
                activeIndex={activeIndex}
                activeShape={PieChartToolTipHOC({ label: labels[activeIndex] })}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(-1)}
                stroke={PIE_STROKE_COLOR}
              >
                {activeIndex === -1 && (
                  <Label
                    position="center"
                    content={({ viewBox }) => (
                      <PieChartLabel value={calculateSum()} viewBox={viewBox} />
                    )}
                  />
                )}
                {indexBy.map((label, index) => (
                  <Cell
                    key={label}
                    fill={ColorTheme[index]}
                    opacity={getOpacity(index)}
                  />
                ))}
              </Pie>
              {isAllZero(parsedData) && (
                <Pie
                  data={[{ name: 'No data', value: 1 }]}
                  dataKey="value"
                  innerRadius="55%"
                  paddingAngle={1}
                  fill="var(--bm-color-surface-subdued)"
                  isAnimationActive={false}
                  stroke={PIE_STROKE_COLOR}
                ></Pie>
              )}
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-2/3 p-2">
          <PieChartLegend
            data={parsedData}
            colors={ColorTheme}
            setActiveIndex={setActiveIndex}
            labels={labels}
          />
        </div>
      </div>
    )
  },
  isEqual,
)
